/**
 * Campaigns API Service
 * Handles campaign creation, management, execution, responses, and AI
 */

import { apiClient, handleApiError } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface Campaign {
  id: string;
  name: string;
  description?: string;
  organization_id: number;
  program_id?: number;
  group_id: string;
  group_name: string;
  campaign_type: 'outreach' | 'reminder' | 'survey' | 'chatflow';
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  start_date?: string;
  end_date?: string;
  schedule_config?: Record<string, any>;
  total_members: number;
  engagement_count: number;
  created_by: number;
  created_at: string;
  last_updated_at?: string;
  channels?: string[];
  social_platforms?: string[];
  social_content?: string;
  hashtags?: string;
  ai_config?: AIConfig;
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  organization_id: number;
  program_id?: number;
  group_id: string;
  campaign_type: 'outreach' | 'reminder' | 'survey' | 'chatflow';
  start_date?: string;
  end_date?: string;
  engagement_ids: string[];
  schedule_config?: Record<string, any>;
  channels?: string[];
  social_platforms?: string[];
  social_content?: string;
  hashtags?: string;
  chatflow_id?: string;
}

export interface UpdateCampaignRequest {
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  status?: 'draft' | 'active' | 'completed' | 'cancelled';
  engagement_ids?: string[];
  schedule_config?: Record<string, any>;
  channels?: string[];
  social_platforms?: string[];
  social_content?: string;
  hashtags?: string;
  campaign_type?: string;
  group_id?: string;
  ai_config?: AIConfig;
  chatflow_id?: string;
}

export interface Engagement {
  id: string;
  name: string;
  description?: string;
  organization_id: number;
  engagement_type: 'sms' | 'email' | 'survey' | 'chatflow' | 'whatsapp' | 'rcs' | 'voice' | 'social';
  content_template: string;
  survey_id?: string;
  chatflow_id?: string;
  wait_for_response: boolean;
  timeout_hours: number;
  status?: string;
  scheduled_at?: string;
  created_at: string;
  // Composer-state persistence — round-tripped so drafts rehydrate on edit.
  group_id?: string | null;
  asset_ids?: number[];
  email_subject?: string | null;
}

export interface CreateEngagementRequest {
  name: string;
  description?: string;
  organization_id: number;
  engagement_type: 'sms' | 'email' | 'survey' | 'chatflow' | 'whatsapp' | 'rcs' | 'voice' | 'social';
  content_template: string;
  survey_id?: string;
  chatflow_id?: string;
  wait_for_response?: boolean;
  timeout_hours?: number;
  group_id?: string | null;
  asset_ids?: number[];
  email_subject?: string | null;
}

export interface CampaignStats {
  campaign_id: string;
  campaign_name: string;
  total_members: number;
  messages_sent: number;
  messages_delivered: number;
  messages_failed: number;
  responses_received: number;
  response_rate: number;
  total_cost_dollars: number;
  avg_response_time_minutes?: number;
  completion_rate: number;
}

// Campaign Members for individual send
export interface CampaignMember {
  member_id: number;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  language: string;
  send_status: string;
  messages_sent: number;
  preferred_channel?: string;
  fallback_channels?: string[];
  channel_attempts?: Record<string, string>;
}

// Campaign Sends & Progress
export interface CampaignSend {
  id: string;
  campaign_id: string;
  engagement_id?: string;
  channel: string;
  bulk_communication_id?: number;
  status: string;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  member_count: number;
  sent_count: number;
  failed_count: number;
  created_at?: string;
}

export interface CampaignProgress {
  campaign_id: string;
  total_sends: number;
  completed: number;
  in_progress: number;
  failed: number;
  pending: number;
  total_members: number;
  total_sent: number;
  total_failed: number;
  overall_status: string;
}

// Campaign Responses & AI
export interface CampaignResponseItem {
  id: string;
  campaign_id: string;
  member_id: string;
  member_name?: string;
  member_phone?: string;
  response_content: string;
  response_channel: string;
  ai_analysis?: Record<string, any>;
  ai_suggested_reply?: string;
  ai_confidence?: number;
  ai_sentiment?: string;
  ai_intent?: string;
  human_reply?: string;
  is_processed: boolean;
  is_escalated: boolean;
  follow_up_required: boolean;
  created_at: string;
}

export interface AIConfig {
  auto_reply_enabled: boolean;
  confidence_threshold: number;
  escalation_rules: any[];
  human_handoff_keywords: string[];
}

export interface ResponseAnalytics {
  campaign_id: string;
  total_responses: number;
  by_sentiment: Record<string, number>;
  by_intent: Record<string, number>;
  unprocessed_count: number;
  escalated_count: number;
  ai_auto_replied_count: number;
}

export interface ConversationMessage {
  id: string;
  source: string;
  direction: string;
  content: string;
  channel: string;
  timestamp: string;
  status?: string;
  ai_generated?: boolean;
}

// Chatflow Types
export interface ChatflowNode {
  id: string;
  type: 'message' | 'question' | 'condition' | 'delay' | 'ai_response' | 'human_handoff' | 'survey' | 'api_call' | 'channel_switch';
  data: Record<string, any>;
  position: { x: number; y: number };
}

export interface ChatflowEdge {
  source: string;
  target: string;
  condition?: string;
}

export interface ChatflowDefinition {
  nodes: ChatflowNode[];
  edges: ChatflowEdge[];
}

export interface Chatflow {
  id: string;
  name: string;
  description?: string;
  organization_id: number;
  flow_definition: ChatflowDefinition;
  is_active: boolean;
  supports_translation: boolean;
  default_language: string;
  node_count: number;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface ChatflowSession {
  id: string;
  chatflow_id: string;
  chatflow_name?: string;
  member_id: string;
  current_node_id?: string;
  status: string;
  channel: string;
  human_takeover: boolean;
  conversation_history?: any[];
  started_at: string;
  last_interaction_at?: string;
  completed_at?: string;
}

export interface ChatflowTemplate {
  id: string;
  name: string;
  description: string;
  node_types: string[];
  flow_definition: ChatflowDefinition;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const campaignsApi = {
  // ---- Campaign CRUD ----
  async listCampaigns(params?: {
    organization_id?: number;
    program_id?: number;
    status_filter?: string;
    limit?: number;
    offset?: number;
  }): Promise<Campaign[]> {
    try {
      const response = await apiClient.get('/api/campaigns', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getCampaign(campaignId: string): Promise<Campaign> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createCampaign(campaign: CreateCampaignRequest): Promise<Campaign> {
    try {
      const response = await apiClient.post('/api/campaigns', campaign);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateCampaign(campaignId: string, updates: UpdateCampaignRequest): Promise<Campaign> {
    try {
      const response = await apiClient.put(`/api/campaigns/${campaignId}`, updates);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async deleteCampaign(campaignId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/campaigns/${campaignId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async launchCampaign(campaignId: string): Promise<{ campaign_id: string; status: string; message: string }> {
    try {
      const response = await apiClient.post(`/api/campaigns/${campaignId}/launch`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Individual Member Send ----
  async getCampaignMembers(campaignId: string): Promise<{ campaign_id: string; content_template: string; engagement_type: string; channels: string[]; members: CampaignMember[]; total: number }> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/members`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async sendIndividualMessage(campaignId: string, memberId: number, channel: string = 'sms', contentOverride?: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/campaigns/${campaignId}/send-individual`, {
        member_id: memberId,
        channel,
        content_override: contentOverride,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getCampaignStats(campaignId: string): Promise<CampaignStats> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/stats`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Campaign Sends & Progress ----
  async getCampaignSends(campaignId: string): Promise<CampaignSend[]> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/sends`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getCampaignProgress(campaignId: string): Promise<CampaignProgress> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/send-progress`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Campaign Responses & AI ----
  async listCampaignResponses(campaignId: string, params?: {
    sentiment?: string;
    intent?: string;
    is_processed?: boolean;
    is_escalated?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CampaignResponseItem[]> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/responses`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async generateAIReply(campaignId: string, responseId: string, sendReply: boolean = false): Promise<{ suggested_reply: string; confidence: number; sent: boolean }> {
    try {
      const response = await apiClient.post(`/api/campaigns/${campaignId}/responses/${responseId}/ai-reply`, { send_reply: sendReply });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async sendHumanReply(campaignId: string, responseId: string, message: string, channel: string = 'sms'): Promise<any> {
    try {
      const response = await apiClient.post(`/api/campaigns/${campaignId}/responses/${responseId}/human-reply`, { message, send_via_channel: channel });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async escalateResponse(campaignId: string, responseId: string, reason?: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/campaigns/${campaignId}/responses/${responseId}/escalate`, { reason });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async markResponseProcessed(campaignId: string, responseId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/campaigns/${campaignId}/responses/${responseId}/mark-processed`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getResponseAnalytics(campaignId: string): Promise<ResponseAnalytics> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/response-analytics`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateAIConfig(campaignId: string, config: Partial<AIConfig>): Promise<any> {
    try {
      const response = await apiClient.put(`/api/campaigns/${campaignId}/ai-config`, config);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getMemberConversation(campaignId: string, memberId: string): Promise<ConversationMessage[]> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/conversations/${memberId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Engagements ----
  async createEngagement(engagement: CreateEngagementRequest): Promise<Engagement> {
    try {
      const response = await apiClient.post('/api/campaigns/engagements', engagement);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async listEngagements(params?: {
    organization_id?: number;
    engagement_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<Engagement[]> {
    try {
      const response = await apiClient.get('/api/campaigns/engagements', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getEngagement(engagementId: string): Promise<Engagement> {
    try {
      const response = await apiClient.get(`/api/campaigns/engagements/${engagementId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateEngagement(engagementId: string, updates: Partial<CreateEngagementRequest>): Promise<Engagement> {
    try {
      const response = await apiClient.put(`/api/campaigns/engagements/${engagementId}`, updates);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async deleteEngagement(engagementId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/campaigns/engagements/${engagementId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Engagement Individual Send ----
  async getEngagementGroupMembers(engagementId: string, groupId: string): Promise<{ engagement_id: string; content_template: string; engagement_type: string; channels: string[]; members: CampaignMember[]; total: number }> {
    try {
      const response = await apiClient.get(`/api/campaigns/engagements/${engagementId}/group-members`, { params: { group_id: groupId } });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async sendEngagementIndividual(engagementId: string, groupId: string, memberId: number, channel: string = 'sms', contentOverride?: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/campaigns/engagements/${engagementId}/send-individual`, {
        group_id: groupId,
        member_id: memberId,
        channel,
        content_override: contentOverride,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Campaign-Engagement Linking ----
  async setCampaignEngagements(campaignId: string, links: CampaignEngagementLink[]): Promise<any> {
    try {
      const response = await apiClient.put(`/api/campaigns/${campaignId}/engagements`, links);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getCampaignEngagements(campaignId: string): Promise<{ campaign_id: string; steps: any[]; total: number }> {
    try {
      const response = await apiClient.get(`/api/campaigns/${campaignId}/engagements`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export interface CampaignEngagementLink {
  engagement_id: string;
  engagement_order: number;
  delay_hours?: number;
  condition_type?: string;
  condition_config?: Record<string, any>;
  is_ab_test?: boolean;
  ab_variant?: string;
  ab_percentage?: number;
  is_parallel?: boolean;
  timeout_hours?: number;
  timeout_action?: string;
  channel_override?: string;
}

// ============================================================================
// CHATFLOWS API
// ============================================================================

export const chatflowsApi = {
  async listChatflows(params?: {
    organization_id?: number;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<Chatflow[]> {
    try {
      const response = await apiClient.get('/api/chatflows', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getChatflow(chatflowId: string): Promise<Chatflow> {
    try {
      const response = await apiClient.get(`/api/chatflows/${chatflowId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async createChatflow(chatflow: {
    name: string;
    description?: string;
    organization_id: number;
    flow_definition: ChatflowDefinition;
    supports_translation?: boolean;
    default_language?: string;
  }): Promise<Chatflow> {
    try {
      const response = await apiClient.post('/api/chatflows', chatflow);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateChatflow(chatflowId: string, updates: {
    name?: string;
    description?: string;
    flow_definition?: ChatflowDefinition;
    is_active?: boolean;
    supports_translation?: boolean;
  }): Promise<Chatflow> {
    try {
      const response = await apiClient.put(`/api/chatflows/${chatflowId}`, updates);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async deleteChatflow(chatflowId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/chatflows/${chatflowId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async duplicateChatflow(chatflowId: string): Promise<Chatflow> {
    try {
      const response = await apiClient.post(`/api/chatflows/${chatflowId}/duplicate`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async activateChatflow(chatflowId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/chatflows/${chatflowId}/activate`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async deactivateChatflow(chatflowId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/chatflows/${chatflowId}/deactivate`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getTemplates(): Promise<ChatflowTemplate[]> {
    try {
      const response = await apiClient.get('/api/chatflows/templates');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getChatflowSessions(chatflowId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ChatflowSession[]> {
    try {
      const response = await apiClient.get(`/api/chatflows/${chatflowId}/sessions`, { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getSession(sessionId: string): Promise<ChatflowSession> {
    try {
      const response = await apiClient.get(`/api/chatflows/sessions/${sessionId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async humanTakeover(sessionId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/chatflows/sessions/${sessionId}/human-takeover`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async releaseSession(sessionId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/chatflows/sessions/${sessionId}/release`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async sendSessionMessage(sessionId: string, message: string): Promise<any> {
    try {
      const response = await apiClient.post(`/api/chatflows/sessions/${sessionId}/send-message`, { message });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
